import * as React from "react";
import * as AvatarPrimitive from "@radix-ui/react-avatar";

import { cn } from "@/lib/utils";

// Stock placeholder URLs that arrived via legacy migrations and shouldn't
// render as real photos. When `src` matches one we drop it so radix
// falls through to <AvatarFallback> (which renders the user's initials).
// Keep the list tight — we want this to ignore known junk, not hide
// real user-uploaded avatars.
const PLACEHOLDER_AVATAR_PATTERNS = [
  // Every Skymark-migrated user got this same supabase URL; once the
  // data cleanup runs and rows are NULLed, this entry can come out.
  "/avatars/public/logoipsum.png",
];

function isPlaceholderAvatar(src: unknown): boolean {
  if (typeof src !== "string" || src.length === 0) return false;
  return PLACEHOLDER_AVATAR_PATTERNS.some((p) => src.includes(p));
}

function Avatar({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Root>) {
  return (
    <AvatarPrimitive.Root
      data-slot="avatar"
      className={cn(
        "relative flex size-8 shrink-0 overflow-hidden rounded-full",
        className
      )}
      {...props}
    />
  );
}

function AvatarImage({
  className,
  src,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Image>) {
  const safeSrc = isPlaceholderAvatar(src) ? undefined : src;
  return (
    <AvatarPrimitive.Image
      data-slot="avatar-image"
      className={cn("aspect-square size-full", className)}
      src={safeSrc}
      {...props}
    />
  );
}

function AvatarFallback({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Fallback>) {
  return (
    <AvatarPrimitive.Fallback
      data-slot="avatar-fallback"
      className={cn(
        "bg-muted flex size-full items-center justify-center rounded-full",
        className
      )}
      {...props}
    />
  );
}

export { Avatar, AvatarImage, AvatarFallback };
